import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  signal,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

export interface YearQuarterSelection {
  year: number;
  quarter: number;
  startDate: Date;
  endDate: Date;
}

@Component({
  selector: 'ups-year-quarter-picker',
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="d-flex align-items-center gap-2">
      <!-- Year Dropdown -->
      <select
        [(ngModel)]="selectedYear"
        (ngModelChange)="onSelectionChange()"
        class="form-select"
        [style.width.px]="yearDropdownWidth()"
      >
        @for (year of availableYears(); track year) {
          <option [ngValue]="year">{{ year }}</option>
        }
      </select>

      <!-- Quarter Dropdown -->
      <select
        [(ngModel)]="selectedQuarter"
        (ngModelChange)="onSelectionChange()"
        class="form-select"
        [style.width.px]="quarterDropdownWidth()"
      >
        @for (quarter of quarters; track quarter.value) {
          <option [ngValue]="quarter.value">{{ quarter.label }}</option>
        }
      </select>
    </div>
  `,
})
export class YearQuarterPickerComponent {
  // Configuration inputs
  readonly yearDropdownWidth = input<number>(100);
  readonly quarterDropdownWidth = input<number>(120);
  readonly startYear = input<number>(2020);
  readonly endYear = input<number>(new Date().getFullYear() + 5);

  // Output
  readonly selectionChanged = output<YearQuarterSelection | null>();

  // Internal state
  selectedYear = signal<number | null>(null);
  selectedQuarter = signal<number | null>(null);

  // Available years
  readonly availableYears = computed(() => {
    const start = this.startYear();
    const end = this.endYear();
    const years: number[] = [];
    for (let year = start; year <= end; year++) {
      years.push(year);
    }
    return years.reverse(); // Most recent first
  });

  // Quarter options
  readonly quarters = [
    { value: 1, label: 'Q1 (Jan-Mar)' },
    { value: 2, label: 'Q2 (Apr-Jun)' },
    { value: 3, label: 'Q3 (Jul-Sep)' },
    { value: 4, label: 'Q4 (Oct-Dec)' },
  ];

  onSelectionChange(): void {
    const year = this.selectedYear();
    const quarter = this.selectedQuarter();

    if (year && quarter) {
      const selection = this.createYearQuarterSelection(year, quarter);
      this.selectionChanged.emit(selection);
    } else {
      this.selectionChanged.emit(null);
    }
  }

  private createYearQuarterSelection(
    year: number,
    quarter: number
  ): YearQuarterSelection {
    // Calculate start and end dates for the quarter
    const quarterStartMonth = (quarter - 1) * 3; // Q1=0, Q2=3, Q3=6, Q4=9

    const startDate = new Date(year, quarterStartMonth, 1);
    const endDate = new Date(year, quarterStartMonth + 3, 0); // Last day of the quarter

    return {
      year,
      quarter,
      startDate,
      endDate,
    };
  }

  // Public method to set selection programmatically
  setSelection(year: number, quarter: number): void {
    this.selectedYear.set(year);
    this.selectedQuarter.set(quarter);
    this.onSelectionChange();
  }

  // Public method to clear selection
  clearSelection(): void {
    this.selectedYear.set(null);
    this.selectedQuarter.set(null);
    this.selectionChanged.emit(null);
  }
}
